import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";

import { ErrorBoundary } from "react-error-boundary";
import { TanStackReactQueryDevelopmentTools } from "./components/developmentTools";
import BoundaryFallback from "./components/error-fallback/BoundaryFallback";
import { ThemeProvider, Toaster } from "./components/ui";
import { AuthProvider, useAuth } from "./pages/auth/auth";
import { router } from "./router/router";
import { queryClient } from "./router/queryClient";
import { isTrustedRedirectHost } from "./utils/trustedOrigins";
import "react-datepicker/dist/react-datepicker.css";
import { useEffect } from "react";

const isTrustedReferrer = (): boolean => {
  if (!document.referrer) return false;
  try {
    const referrerHost = new URL(document.referrer).hostname;
    return isTrustedRedirectHost(referrerHost);
  } catch {
    return false;
  }
};

const Router = () => {
  const auth = useAuth();
  return <RouterProvider router={router} context={{ queryClient, auth }} />;
};

export const App = () => {
  useEffect(() => {
    // On page load, check if tokens are in URL (from subdomain redirect)
    const params = new URLSearchParams(window.location.search);
    const access = params.get("access_token");
    const refresh = params.get("refresh_token");

    if (access && refresh) {
      if (isTrustedReferrer()) {
        try {
          localStorage.setItem("access_token", access);
          localStorage.setItem("refresh_token", refresh);
        } catch (e) {
          console.error("[App] Failed to store tokens:", e);
        }
      } else {
        console.warn(
          "[App] Ignored access/refresh token params from an untrusted or missing referrer.",
        );
      }

      // Always strip tokens from the URL, trusted or not, so they never
      // persist in browser history / logs / screenshots.
      const cleanUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
      window.history.replaceState({}, "", cleanUrl);
    }
  }, []);
  return (
    <ErrorBoundary FallbackComponent={BoundaryFallback}>
      <QueryClientProvider client={queryClient}>
        <TanStackReactQueryDevelopmentTools
          initialIsOpen={false}
          buttonPosition="bottom-right"
        />
        <ThemeProvider>
          <Toaster />
          <AuthProvider>
            <Router />
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};
